import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { logout, selectRelation } from '../actions/constants'

export type BiometricsType = 'fingerprint' | 'face'

export interface IBiometricsSlice {
  supported: boolean
  isEnrolled: boolean
  available: {
    fingerprint: boolean
    face: boolean
  }
  availableTypes: BiometricsType[]

  enabled: {
    fingerprint: boolean
    face: boolean
  }

  lastCheckedAt?: number
}

const initialState: IBiometricsSlice = {
  supported: false,
  isEnrolled: false,
  available: {
    fingerprint: false,
    face: false
  },
  availableTypes: [],
  enabled: {
    fingerprint: false,
    face: false
  },
  lastCheckedAt: undefined
}

export const biometricsSlice = createSlice({
  name: 'biometrics',
  initialState,
  reducers: {
    setAvailability: (
      state,
      action: PayloadAction<{
        supported: boolean
        isEnrolled: boolean
        availableTypes: BiometricsType[]
        checkedAt?: number
      }>
    ) => {
      const { supported, isEnrolled, availableTypes, checkedAt } = action.payload

      state.supported = supported
      state.isEnrolled = isEnrolled
      state.availableTypes = availableTypes

      state.available = {
        fingerprint: availableTypes.includes('fingerprint'),
        face: availableTypes.includes('face')
      };

      (['fingerprint', 'face'] as BiometricsType[]).forEach((t) => {
        if (!state.available[t]) {
          state.enabled[t] = false
        }
      })
      state.lastCheckedAt = checkedAt ?? Date.now()
    },
    setEnabled: (
      state,
      action: PayloadAction<{ type: BiometricsType; enabled: boolean }>
    ) => {
      const { type, enabled } = action.payload

      // Guard: only allow enabling if the type is available
      state.enabled[type] = enabled && state.available[type]
    },
    resetBiometrics: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)
      .addCase(selectRelation, () => initialState)
  }
})

export const { setAvailability, setEnabled, resetBiometrics } = biometricsSlice.actions

export default biometricsSlice.reducer
